// frontend/src/components/OwnerDashboard/AiInsightsCard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../api/baseURL';

const AiInsightsCard = ({ vehicles }) => {
    const { token } = useAuth();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('overview'); // 'overview' | 'diagnose'
    const [symptoms, setSymptoms] = useState('');
    const [diagnosis, setDiagnosis] = useState(null);
    const [diagnosing, setDiagnosing] = useState(false);
    const [error, setError] = useState('');
    // The chosen vehicle is held as an id, not an object. `vehicles` arrives
    // from useQuery with a `= []` default, which is a fresh array on every
    // render — keying the fetch below on a string makes it immune to that.
    const [selectedId, setSelectedId] = useState('');

    const selectedVehicle = vehicles?.find((v) => v._id === selectedId) || null;

    // Default to the first vehicle, and keep the selection valid if the list
    // changes underneath us (a vehicle sold or removed).
    useEffect(() => {
        if (!vehicles?.length) return;
        setSelectedId((cur) =>
            vehicles.some((v) => v._id === cur) ? cur : vehicles[0]._id
        );
    }, [vehicles]);

    // No automatic fetch. The analysis runs only when the owner asks for it:
    // it costs a call against a small monthly allowance, and firing one for
    // every vehicle simply because the dashboard mounted spent that allowance
    // on cars nobody was looking at.

    // Switching vehicles must drop the previous car's results — showing a
    // diagnosis for the Corolla under the Hilux's name is worse than showing
    // nothing.
    const handleVehicleChange = (id) => {
        setSelectedId(id);
        setAnalysis(null);
        setDiagnosis(null);
        setSymptoms('');
        setError('');
    };

    const fetchAnalysis = async () => {
        if (!selectedId || !token) return;
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${API_URL}/ai/analyze/${selectedId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAnalysis(res.data);
        } catch (err) {
            console.error('AI Analysis error:', err);
            // 403 is the monthly allowance, not a failure — say which it is.
            setError(err.response?.status === 403
                ? (err.response?.data?.error || 'Limite d’analyses atteinte pour ce mois.')
                : 'Analyse indisponible pour le moment. Réessayez.');
            setAnalysis(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDiagnose = async () => {
        if (!symptoms.trim()) return;
        setDiagnosing(true);
        try {
            const res = await axios.post(`${API_URL}/ai/diagnose`,
                { vehicleId: selectedVehicle?._id, symptoms },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDiagnosis(res.data);
        } catch (err) {
            setDiagnosis({ error: err.response?.data?.error || 'Échec du diagnostic' });
        } finally {
            setDiagnosing(false);
        }
    };

    const getHealthColor = (score) => {
        if (score >= 80) return 'text-green-400';
        if (score >= 60) return 'text-yellow-400';
        if (score >= 40) return 'text-orange-400';
        return 'text-red-400';
    };

    const getHealthBg = (score) => {
        if (score >= 80) return 'from-green-500 to-emerald-600';
        if (score >= 60) return 'from-yellow-500 to-orange-500';
        if (score >= 40) return 'from-orange-500 to-red-500';
        return 'from-red-500 to-red-700';
    };

    if (!vehicles || vehicles.length === 0) return null;

    return (
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                        <span className="text-xl">🤖</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-white">Analyse IA du véhicule</h3>
                        <p className="text-xs text-slate-400">
                            {analysis?.confidence === 'high' ? '✓ Basée sur votre historique d’entretien' :
                                analysis?.confidence === 'medium' ? '○ Historique limité' :
                                    '◌ Ajoutez des entretiens pour affiner l’analyse'}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setView('overview')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${view === 'overview' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                    >📊 Analyse</button>
                    <button
                        onClick={() => setView('diagnose')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${view === 'diagnose' ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                    >🩺 Diagnostic</button>
                </div>
            </div>

            {/* Vehicle picker. Hidden for a single vehicle — a one-option
                dropdown is noise, and the name is shown below it anyway. */}
            {vehicles.length > 1 && (
                <div className="mb-4">
                    <label htmlFor="ai-vehicle" className="block text-xs text-slate-400 mb-1.5">
                        Véhicule à analyser
                    </label>
                    <select
                        id="ai-vehicle"
                        value={selectedId}
                        onChange={(e) => handleVehicleChange(e.target.value)}
                        disabled={loading || diagnosing}
                        className="w-full bg-slate-700/50 border border-slate-600 rounded-xl px-3 py-2.5 text-white text-sm
                                   outline-none focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {vehicles.map((v) => (
                            <option key={v._id} value={v._id} className="bg-slate-800">
                                {[v.plateNumber, [v.brand, v.model].filter(Boolean).join(' ')]
                                    .filter(Boolean).join(' — ')}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* With one vehicle there is no picker, so name it here — otherwise
                the analysis has no visible subject. */}
            {vehicles.length === 1 && selectedVehicle && (
                <p className="text-xs text-slate-400 mb-4">
                    {[selectedVehicle.plateNumber,
                      [selectedVehicle.brand, selectedVehicle.model].filter(Boolean).join(' ')]
                        .filter(Boolean).join(' — ')}
                </p>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                    <span className="ml-3 text-slate-400">Analyse de l’historique…</span>
                </div>
            )}

            {/* Idle: nothing runs until the owner asks for it. */}
            {!loading && view === 'overview' && !analysis && (
                <div className="text-center py-8">
                    <span className="text-4xl">🔎</span>
                    <p className="text-slate-300 mt-3 mb-1">Analyse de l’état du véhicule</p>
                    <p className="text-xs text-slate-500 mb-4">
                        Basée sur votre historique d’entretien confirmé.
                    </p>
                    <button
                        onClick={fetchAnalysis}
                        disabled={!selectedId}
                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-600
                                   text-white font-medium rounded-xl transition-all"
                    >
                        Lancer l’analyse
                    </button>
                    {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
                </div>
            )}

            {/* Overview View */}
            {!loading && view === 'overview' && analysis && (
                <div className="space-y-4">
                    {/* Health Score */}
                    {analysis.analysis?.healthScore >= 0 && (
                        <div className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl">
                            <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getHealthBg(analysis.analysis.healthScore)} flex items-center justify-center`}>
                                <span className="text-white text-xl font-bold">{analysis.analysis.healthScore}</span>
                            </div>
                            <div className="flex-1">
                                <p className={`text-lg font-bold ${getHealthColor(analysis.analysis.healthScore)}`}>
                                    {analysis.analysis.healthScore >= 80 ? 'Excellent état' :
                                        analysis.analysis.healthScore >= 60 ? 'Bon état' :
                                            analysis.analysis.healthScore >= 40 ? 'À surveiller' : 'Critique'}
                                </p>
                                <p className="text-sm text-slate-400">{analysis.analysis.summary}</p>
                            </div>
                        </div>
                    )}

                    {/* Predictions */}
                    {analysis.analysis?.predictions?.length > 0 && (
                        <div>
                            <h4 className="text-white font-medium mb-2">🔮 Prévisions</h4>
                            <div className="space-y-2">
                                {analysis.analysis.predictions.slice(0, 3).map((pred, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-3 bg-slate-700/30 rounded-xl">
                                        <div>
                                            <p className="text-white font-medium">{pred.component}</p>
                                            <p className="text-xs text-slate-400">{pred.prediction}</p>
                                        </div>
                                        <span className={`text-xs font-bold px-2 py-1 rounded ${pred.urgency === 'high' ? 'bg-red-500/20 text-red-400' :
                                                pred.urgency === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                                    'bg-green-500/20 text-green-400'
                                            }`}>{pred.timeframe}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Recommendations */}
                    {analysis.analysis?.recommendations?.length > 0 && (
                        <div>
                            <h4 className="text-white font-medium mb-2">💡 Recommandations</h4>
                            <ul className="space-y-1">
                                {analysis.analysis.recommendations.slice(0, 3).map((rec, idx) => (
                                    <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                                        <span className="text-green-400">✓</span> {rec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* No data message */}
                    {analysis.dataPoints === 0 && (
                        <div className="text-center py-6">
                            <span className="text-4xl">📋</span>
                            <p className="text-slate-400 mt-2">
                                {analysis.pendingLogs > 0
                                    ? 'Entretiens en attente de confirmation'
                                    : "Aucun historique d'entretien confirmé"}
                            </p>
                            <p className="text-xs text-slate-500">
                                {analysis.pendingLogs > 0
                                    ? 'Confirmez-les pour obtenir une analyse IA'
                                    : 'Ajoutez des entretiens pour obtenir une analyse IA'}
                            </p>
                        </div>
                    )}

                    {/* Re-running is an explicit choice too. A result that has
                        not changed is served from cache and costs nothing. */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                        <span className="text-xs text-slate-500">
                            {analysis.cached ? 'Résultat enregistré' : 'Analyse à jour'}
                            {analysis.lastUpdated
                                && ` · ${new Date(analysis.lastUpdated).toLocaleDateString('fr-FR')}`}
                            {analysis.stale && ' · limite mensuelle atteinte'}
                        </span>
                        <button
                            onClick={fetchAnalysis}
                            className="text-xs text-indigo-400 hover:text-indigo-300"
                        >
                            ↻ Actualiser
                        </button>
                    </div>
                    {error && <p className="text-red-400 text-sm">{error}</p>}
                </div>
            )}

            {/* Diagnose View */}
            {view === 'diagnose' && (
                <div className="space-y-4">
                    <textarea
                        value={symptoms}
                        onChange={(e) => setSymptoms(e.target.value)}
                        placeholder="Décrivez le problème rencontré… (ex. : la voiture vibre au freinage, le moteur cliquette au démarrage)"
                        className="w-full bg-slate-700/50 border border-slate-600 rounded-xl p-3 text-white placeholder-slate-400 text-sm resize-none"
                        rows={3}
                    />
                    <button
                        onClick={handleDiagnose}
                        disabled={diagnosing || !symptoms.trim()}
                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-600 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        {diagnosing ? (
                            <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div> Analyse en cours…</>
                        ) : (<>🔍 Lancer le diagnostic</>)}
                    </button>

                    {/* Results */}
                    {diagnosis && !diagnosis.error && (
                        <div className="space-y-3 pt-3 border-t border-slate-700">
                            <h4 className="text-white font-medium">Causes possibles :</h4>
                            {diagnosis.possibleCauses?.map((cause, idx) => (
                                <div key={idx} className="p-3 bg-slate-700/30 rounded-xl flex justify-between items-center">
                                    <span className="text-white">{cause.cause}</span>
                                    <span className={`text-xs font-bold px-2 py-1 rounded ${cause.urgency === 'high' ? 'bg-red-500/20 text-red-400' :
                                            cause.urgency === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                                'bg-green-500/20 text-green-400'
                                        }`}>{cause.likelihood}</span>
                                </div>
                            ))}
                            {diagnosis.shouldVisitWorkshop && (
                                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                                    <p className="text-orange-400 font-medium">⚠️ Visite en atelier recommandée</p>
                                </div>
                            )}
                            {diagnosis.estimatedCost && (
                                <p className="text-sm text-slate-400">💰 Coût estimé : {diagnosis.estimatedCost}</p>
                            )}
                        </div>
                    )}
                    {diagnosis?.error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                            <p className="text-red-400">{diagnosis.error}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AiInsightsCard;

